
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useState, useEffect } from "react";
import { useProfileStore } from "./ProfileData";
import { useToast } from "@/components/ui/use-toast";
import { ProfileBioHeader } from "./bio/ProfileBioHeader";
import { ProfileBioEditor } from "./bio/ProfileBioEditor";
import { ProfileBioDisplay } from "./bio/ProfileBioDisplay";
import { User, Calendar, CheckCircle } from "lucide-react";
import { useFreelancerStore } from "@/store/freelancerStore";
import { format } from "date-fns";
import { useUserStore } from "@/store/userStore";

interface ProfileHeaderProps {
  formData: {
    bio: string;
    _id: string;
  };
  accessToken: string | null;
  freelancerId?: string | null;
}

export const ProfileBio = ({
  formData,
  accessToken,
  freelancerId
}: ProfileHeaderProps) => {
    const { profile, fetchFreelancerProfile, updateFreelancerProfile } = useFreelancerStore();
    const { user } = useUserStore();
    const profileData = profile;

  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [bioText, setBioText] = useState(profileData?.bio);
  const [isValid, setIsValid] = useState(true);
  
  const MAX_BIO_LENGTH = 500;

  useEffect(() => {
    setBioText(profileData?.bio);
  }, [profileData?.bio]);

  useEffect(() => {
    setIsValid(bioText?.trim().length > 0 && bioText?.length <= MAX_BIO_LENGTH);
  }, [bioText, MAX_BIO_LENGTH]);

  const handleSave = async () => {
    if (!isValid) return;
    
    await updateFreelancerProfile(formData._id,{bio: bioText}, accessToken);
    setIsEditing(false);
    
    toast({
      title: "Bio Updated",
      description: "Your professional bio has been updated successfully.",
      variant: "success"
    });
  };

  const handleCancel = () => {
    setBioText(profileData?.bio);
    setIsValid(true);
    setIsEditing(false);
  };

  return (
    <Card className="hover:shadow-md transition-all duration-300 border-blue-100">
      <CardHeader className="pb-2">
        <ProfileBioHeader 
          isEditing={isEditing} 
          setIsEditing={setIsEditing} 
          freelancerId={freelancerId}
        />
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {isEditing ? (
            <ProfileBioEditor
              isEditing={isEditing}
              bioText={bioText}
              setBioText={setBioText}
              isValid={isValid}
              MAX_BIO_LENGTH={MAX_BIO_LENGTH}
              handleSave={handleSave}
              handleCancel={handleCancel}
            />
          ) : (
            <>
              <ProfileBioDisplay bio={profileData?.bio} />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 pt-4 border-t border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Member Since</h4>
                    <p className="text-sm text-gray-600">{profileData?.createdAt ? format(new Date(profileData?.createdAt), "MMMM yyyy") : "-"}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Identity Verified</h4>
                    <p className="text-sm text-gray-600">{user?.isDetailsSaved ? "Verified ": "Unverified "} professional</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
